import { Link } from 'react-router-dom';
import type { Task } from '../lib/types';
import { STATUS_COLORS } from '../lib/types';

interface Props {
  ancestors: Task[];
  current: Task;
  descendants: Task[];
  projectSlug: string;
}

/**
 * Visual chain of continuation: ancestors → current → descendants.
 * Each pill is clickable to navigate to that task. If the chain is long
 * we still render flat (no collapsing for v1; can paginate later).
 */
export default function TaskChain({
  ancestors,
  current,
  descendants,
  projectSlug,
}: Props) {
  if (ancestors.length === 0 && descendants.length === 0) return null;

  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2 mb-3 overflow-x-auto scrollbar-thin">
      <div className="text-[10px] uppercase tracking-wide text-slate-500 mb-1">
        Continuation chain
      </div>
      <div className="flex items-center gap-1.5 whitespace-nowrap">
        {ancestors.map((t, i) => (
          <ChainPill
            key={t.id}
            task={t}
            projectSlug={projectSlug}
            isLast={i < ancestors.length - 1}
            isCurrent={false}
          />
        ))}
        <ChainPill
          task={current}
          projectSlug={projectSlug}
          isLast={descendants.length > 0}
          isCurrent
        />
        {descendants.map((t, i) => (
          <ChainPill
            key={t.id}
            task={t}
            projectSlug={projectSlug}
            isLast={i < descendants.length - 1}
            isCurrent={false}
          />
        ))}
      </div>
    </div>
  );
}

function ChainPill({
  task,
  projectSlug,
  isLast,
  isCurrent,
}: {
  task: Task;
  projectSlug: string;
  isLast: boolean;
  isCurrent: boolean;
}) {
  const content = (
    <span
      className={`inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-xs ${
        isCurrent
          ? 'border-slate-800 bg-slate-800 text-white font-medium'
          : STATUS_COLORS[task.status]
      }`}
      title={task.title}
    >
      <span className="font-mono text-[10px] opacity-70">
        {task.id.slice(0, 6)}
      </span>
      <span className="max-w-[180px] truncate">{task.title}</span>
    </span>
  );
  return (
    <>
      {isCurrent ? content : (
        <Link to={`/p/${projectSlug}/tasks/${task.id}`}>{content}</Link>
      )}
      {isLast && <span className="text-slate-400 text-xs">→</span>}
    </>
  );
}
